"use client"

import { normalizeTextEncoding } from "@/lib/util/text-encoding"
import { normalizeUnitType } from "@/lib/util/normalize-unit"
import { StoreProductVariant } from "@/types/store"
import { Dialog, Transition } from "@headlessui/react"
import { Fragment, useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Input } from "@/components/ui/input"
import { Button } from "@medusajs/ui"

type CalculateModalProps = {
  isOpen: boolean
  closeModal: () => void
  variant: StoreProductVariant
  unitPriceType?: string | null
}

const CalculateModal = ({
  isOpen,
  closeModal,
  variant,
  unitPriceType,
}: CalculateModalProps) => {
  const { t } = useTranslation()
  const [targetArea, setTargetArea] = useState<number>(0)

  const result = useMemo(() => {
    const areaWithWaste = targetArea * 1.10
    const roundedArea = Math.ceil(areaWithWaste)

    const effectiveUnitType = unitPriceType || variant.product?.unit_price_type_name || ""
    const normalizedUnit = normalizeUnitType(effectiveUnitType)
    const isPiece = normalizedUnit === "Piece" || (!normalizedUnit && variant.width && variant.length)
    const isM2 = normalizedUnit === "m²"

    // If it's explicitly m2, we don't do piece calculation even if dimensions are present
    if (isPiece && !isM2) {
      const widthInMeters = (variant.width ?? 0) / 100
      const heightInMeters = (variant.length ?? 0) / 100
      const pieceCoverage = widthInMeters * heightInMeters
      
      console.log("Calculation type: Piece", {
        targetArea,
        pieceCoverage,
        widthInMeters,
        heightInMeters
      })

      if (pieceCoverage === 0) return 0
      
      // Calculation Logic for Products Sold by Piece:
      // 1. Required Area With Waste: customer input * 1.10
      // 2. Rounded Area: ceil(Required Area With Waste)
      // 3. Required Pieces: Rounded Area / pieceCoverage
      // 4. Result: ceil(Required Pieces)
      const requiredAreaWithWaste = targetArea * 1.10
      const roundedAreaVal = Math.ceil(requiredAreaWithWaste)
      const requiredPieces = roundedAreaVal / pieceCoverage
      return Math.ceil(requiredPieces)
    }

    console.log("Calculation type: m²", {
      targetArea,
      areaWithWaste,
      roundedArea
    })

    // Calculation Logic for Products Sold by m²:
    // 1. Take customer input
    // 2. Add 10% waste allowance
    // 3. Round up to nearest whole number
    return roundedArea
  }, [targetArea, variant, unitPriceType])

  const resultLabel = useMemo(() => {
    const effectiveUnitType = unitPriceType || variant.product?.unit_price_type_name || ""
    const normalizedUnit = normalizeUnitType(effectiveUnitType)
    const isPiece = normalizedUnit === "Piece" || (!normalizedUnit && variant.width && variant.length)
    const isM2 = normalizedUnit === "m²"

    if (isPiece && !isM2) {
      return t("product.recommendedPieces", { 
        defaultValue: "Recommended quantity including 10% waste allowance: {{count}} pieces", 
        count: result 
      })
    }
    return t("product.recommendedArea", { 
      defaultValue: "Recommended quantity including 10% waste allowance: {{count}} m²", 
      count: result 
    })
  }, [result, t, variant, unitPriceType])

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative" style={{ zIndex: 24 }} onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
                <Dialog.Title className="text-lg font-medium text-gray-900">
                  {t("product.calculate")}
                </Dialog.Title>
                <div className="mt-4 flex flex-col gap-4">
                  <p className="text-sm text-muted-foreground">
                    {normalizeTextEncoding(variant.title ?? "")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("product.area", { defaultValue: "Enter the area you want to cover (m²)" })}
                  </p>
                  <Input
                    type="number"
                    name="alan"
                    step="0.01"
                    value={targetArea || ""}
                    onChange={(e) => setTargetArea(Number(e.target.value))}
                  />
                  <div className="rounded-lg bg-orange-50 p-4 text-center">
                    <p className="text-sm text-orange-800">{resultLabel}</p>
                  </div>
                  <Button onClick={closeModal} className="w-full">
                    {t("common.close", { defaultValue: "Kapat" })}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default CalculateModal
